import { Injectable } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, FormArray, AbstractControl } from '@angular/forms';
import { CrudFormItemValidator } from '../../utils/edit-form/crud-form-item-validator.interface';
import { CrudFormFixButton } from '../../utils/edit-form/crud-form-fix-button.config';

@Injectable({
  providedIn: 'root'
})
export class FormService {

  constructor(private formBuilder: FormBuilder) { }

  getDirtyFormValues(form: any): any {
    const dirtyValues = {};
    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key];
      if (currentControl.touched && !currentControl.pristine) {
        dirtyValues[key] = currentControl.controls ? this.getDirtyFormValues(currentControl) : currentControl.value;
      }
    });
    return dirtyValues;
  }

  getActiveErrorKeys(formGroup: FormGroup, fieldName: string, forceShowErrors = false) {
    const control = formGroup.get(fieldName);
    const errors = control?.errors;
    const isTouched = control?.touched;
    const isDirty = control?.dirty;

    if (errors && (isTouched || isDirty || forceShowErrors)) {
      return Object.keys(errors);
    } else {
      return [];
    }
  }

  /**
   * mark form elements as dirty
   *
   * @param {FormGroup} form
   * @memberof FormService
   */
  checkFormValidation(form: FormGroup) {
    for (const key in form.controls) {
      if (key) {
        form.controls[key].markAsDirty();
        form.controls[key].updateValueAndValidity();
      }
    }
  }

  createFormByData(data: { [key: string]: any }): FormGroup {
    const formValue = {};
    for (const [key, value] of Object.entries(data)) {
      formValue[key] = [value];
    }
    return this.formBuilder.group(formValue);
  }

  checkAllFormValidations(form: FormGroup) {
    /* make form elements as dirty */
    // tslint:disable-next-line: forin
    for (const key in form.controls) {
      if (key) {
        const control = form.controls[key];
        if (control instanceof FormControl) {
          this.validateControl(control);
        } else if (control instanceof FormGroup) {
          this.checkFormValidation(control);
        } else if (control instanceof FormArray) {
          const formGroups: FormGroup[] = <FormGroup[]>control.controls;
          for (const formGroup of formGroups) {
            this.checkAllFormValidations(formGroup);
          }
        }
      }
    }
  }

  validateControl(control: AbstractControl) {
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  /**
   * Fix button click event
   *
   * @param form Form group
   * @param fieldName Field name
   * @param field Field configuration
   * @returns Fixed value
   */
  fixAndUpdateFormValue(form: FormGroup, fieldName: string, field: any): any {
    const control = form.get(fieldName);
    if (!control) {
      return null;
    }

    const currentValue = control.value || '';

    const fixedValue = this.fixValidationError(currentValue, field);
    if (fixedValue === currentValue) {
      return null;
    }

    control.setValue(fixedValue);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();

    return fixedValue;
  }

  validationErrorFixConfigs: Record<string, CrudFormItemValidator> = {
    'ERRORS.PATTERN_MESSAGES.MUST_BE_KEBAB_CASE': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.KEBAB_CASE,
      fixFunction: this.convertToKebabCase
    },
    'ERRORS.PATTERN_MESSAGES.PROJECT_MODEL_NAME_ERROR': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.CAPITAL_LETTER,
      fixFunction: this.capitalizeFirstLetter
    },
    'PROJECT_MODULE.PROJECT_NAME_ERROR': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.FIX_NAME,
      fixFunction: this.fixProjectName
    },
    'ERRORS.PATTERN_MESSAGES.MUST_START_WITH_CAPITAL_LETTER': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.CAPITAL_LETTER,
      fixFunction: this.capitalizeFirstLetter
    },
    'ERRORS.PATTERN_MESSAGES.MUST_BE_PASCAL_CASE': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.PASCAL_CASE,
      fixFunction: this.convertToPascalCase
    },
    'ERRORS.PATTERN_MESSAGES.MUST_BE_PASCAL_CASE_LETTERS_ONLY': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.PASCAL_CASE,
      fixFunction: this.convertToPascalCase
    },
    'ERRORS.PATTERN_MESSAGES.MUST_BE_CAMEL_CASE': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.CAMEL_CASE,
      fixFunction: this.convertToCamelCase
    },
    'ERRORS.PATTERN_MESSAGES.MUST_START_WITH_LOWERCASE_LETTER': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.LOWERCASE_LETTER,
      fixFunction: (value) => value.charAt(0).toLowerCase() + value.slice(1)
    },
    'ERRORS.PATTERN_MESSAGES.MUST_BE_TITLE_CASE_MESSAGE': {
      errorKey: 'pattern',
      fixButtonText: CrudFormFixButton.TITLE_CASE,
      fixFunction: this.capitalizeFirstLetter
    }
  };

  /**
   * Get fix button text by error type
   *
   * @param errorMessage Error message
   * @param field Field configuration (optional)
   * @returns Fix button text
   */
  getFixButtonTextByErrorType(errorMessage: string, field?: any): string | null {
    if (!errorMessage) {
      return null;
    }

    if (field) {
      const patternValidator = field.validators?.find(v =>
        (v.errorKey === 'pattern' && v.errorMessage === errorMessage) || v.errorMessage === errorMessage
      );
      if (patternValidator?.fixButtonText) {
        const fixText = patternValidator.fixButtonText.trim();
        return fixText || null;
      }
    }

    // Fall back to global configurations
    const fixConfig = this.validationErrorFixConfigs[errorMessage];
    if (fixConfig?.fixButtonText) {
      const fixText = fixConfig.fixButtonText.trim();
      return fixText || null;
    }

    return null;
  }

  /**
   * Fix validation error
   *
   * @param currentValue Current value
   * @param field Field configuration
   * @returns Fixed value
   */
  fixValidationError(currentValue: any, field: any): any {
    if (!currentValue) {
      return '';
    }

    const validatorWithFixFunction = field.validators?.find(v => v.fixFunction);
    if (validatorWithFixFunction) {

      if (typeof validatorWithFixFunction.fixFunction === 'string') {
        const fixFn = new Function('value', `return (${validatorWithFixFunction.fixFunction})(value);`);
        return fixFn(currentValue);
      } else if (typeof validatorWithFixFunction.fixFunction === 'function') {
        return validatorWithFixFunction.fixFunction(currentValue);
      }
    }

    const patternValidator = field.validators?.find(v => v.errorKey === 'pattern');
    if (!patternValidator) {
      return currentValue;
    }

    if (patternValidator.fixFunction) {
      if (typeof patternValidator.fixFunction === 'string') {
        const fixFn = new Function('value', `return (${patternValidator.fixFunction})(value);`);
        return fixFn(currentValue);
      } else if (typeof patternValidator.fixFunction === 'function') {
        return patternValidator.fixFunction(currentValue);
      }
    }

    const errorMessage = patternValidator.errorMessage;
    const fixConfig = this.validationErrorFixConfigs[errorMessage];
    if (fixConfig && typeof fixConfig.fixFunction === 'function') {
      return fixConfig.fixFunction(currentValue);
    }

    return currentValue;
  }

  /**
   * Converts string to kebab-case format and removes leading numbers
   *
   */
  private convertToKebabCase(value: string): string {
    if (!value) {
      return '';
    }

    const cleanValue = value.replace(/^[^a-zA-Z_]+/, '');

    if (!cleanValue) {
      return '';
    }

    return cleanValue
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
      .replace(/\s+/g, '-')
      .replace(/_+/g, '-')
      .replace(/([A-Z])/g, c => c.toLowerCase())
      .replace(/^-/, '')
      .replace(/-$/, '')
      .toLowerCase();
  }

  /**
   * Converts string to PascalCase format
   */
  private convertToPascalCase(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/[-_\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : '')
      .replace(/^(.)/, (_, c) => c.toUpperCase());
  }

  /**
   * Converts string to camelCase format
   */
  private convertToCamelCase(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/[-_\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : '')
      .replace(/^(.)/, (_, c) => c.toLowerCase());
  }

  /**
   * Capitalizes the first letter of a string and removes leading numbers
   *
   */
  private capitalizeFirstLetter(value: string): string {
    if (!value) {
      return '';
    }

    const cleanValue = value.replace(/^[^a-zA-Z_]+/, '');
    if (!cleanValue) {
      return '';
    }

    if (cleanValue.startsWith('_')) {
      return cleanValue;
    }

    return cleanValue.charAt(0).toUpperCase() + cleanValue.slice(1);
  }

  /**
   * Sanitizes string by removing special characters
   */
  private sanitizeValue(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .trim();
  }

  /**
   * Must start with a letter
   * Can only contain letters, numbers, spaces, underscores, and hyphens
   */
  private fixProjectName(value: string): string {
    if (!value) {
      return '';
    }

    let result = value.replace(/[^a-zA-Z0-9 _-]/g, '');

    if (!/^[a-zA-Z]/.test(result)) {
      result = result.replace(/^[^a-zA-Z]+/, '');
    }

    return result;
  }

}
